import {
    Component,
    type OnDestroy,
    type OnInit,
    DOCUMENT,
    inject,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import {
    ActivatedRoute,
    type ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
} from "@angular/router";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { type NavData, navItems } from "../../_nav";
import { AccountService, type IUser } from "../../core";
import { ClientAccessRegistrationService } from "../../entities/client-access-registration/service/client-access-registration.service";
import { iconSubset } from "../../icons/icon-subset";
import { Event, EventService } from "../../shared/events/events.service";
import { Feature, FeatureService } from "../../shared/feature/feature.service";
import { DefaultLayoutService } from "./default-layout.service";

@Component({
    selector: "app-dashboard",
    templateUrl: "./default-layout.component.html",
    styleUrl: "./default-layout.component.scss",
    standalone: false,
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
    private accountService = inject(AccountService);
    private featureService = inject(FeatureService);
    private eventService = inject(EventService);
    private activatedRoute = inject(ActivatedRoute);
    private router = inject(Router);
    private titleService = inject(Title);
    private defaultLayoutService = inject(DefaultLayoutService);
    private clientAccessRegistrationService = inject(
        ClientAccessRegistrationService,
    );

    public navItems: NavData[];
    public sidebarMinimized = true;
    public element: HTMLElement;
    public currentAccount: IUser;
    currentUserIsAdmin: boolean;
    currentUserIsLimitedAccess: boolean;

    public currentUrl: string;
    public hidenav = false;
    private changes: MutationObserver;
    private destroyed$ = new Subject<void>();
    private forceRefreshOnNextNav = false;

    firmName: string;
    customLogo: string;
    active: boolean;
    loaded: boolean;

    constructor() {
        const _document = inject(DOCUMENT);

        this.changes = new MutationObserver(() => {
            this.sidebarMinimized =
                _document.body.classList.contains("sidebar-minimized");
        });
        this.element = _document.body;
        this.changes.observe(<Element>this.element, {
            attributes: true,
            attributeFilter: ["class"],
        });
    }

    ngOnInit() {
        this.defaultLayoutService.firmInfo$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((firmInfo) => {
                if (firmInfo.logo) {
                    this.customLogo = firmInfo.logo;
                }
                this.firmName = firmInfo.name;
                this.active = firmInfo.active;
                this.loaded = true;
            });

        this.eventService
            .onObservable(Event.REFRESH_NAV)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.refreshNav(true));

        this.router.events
            .pipe(takeUntil(this.destroyed$))
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.currentUrl = event.url;

                    if (
                        this.anyRouteChildHasNavHidden(
                            this.activatedRoute.snapshot,
                        )
                    ) {
                        this.hidenav = true;
                    } else {
                        this.hidenav = false;
                    }

                    if (this.hidenav) {
                        return;
                    }

                    if (event.url === "/") {
                        this.forceRefreshOnNextNav = true;
                        this.router.navigate(["/dashboard"]);
                        return;
                    }

                    const forceRefresh = this.forceRefreshOnNextNav;
                    this.forceRefreshOnNextNav = false;
                    this.refreshNav(forceRefresh);
                }
            });
    }

    refreshNav(force: boolean) {
        if (!this.accountService.hasAuthToken()) {
            this.accountService.authenticate(null);
            this.applyAccountContext(null);
            this.navItems = [];
            return;
        }

        this.accountService.identity(force).then((account) => {
            this.applyAccountContext(account);
            if (account) {
                const filteredItems = this.filterNavMenu(account);
                if (
                    this.accountService.hasAnyAuthority([
                        "ROLE_MANAGE_CLIENT_PORTAL_ACCESS",
                    ])
                ) {
                    this.getPendingRegistrationsCount()
                        .pipe(take(1))
                        .subscribe((count) => {
                            this.navItems = this.updateAccessNavItemWithCount(
                                filteredItems,
                                count,
                            );
                        });
                } else {
                    this.navItems = filteredItems;
                }
            } else {
                this.navItems = [];
            }
        });
    }

    filterNavMenu(account): NavData[] {
        return navItems.filter((nav) => {
            let hasAuth = true;
            let enabled = true;

            if (nav.authorities) {
                hasAuth = this.accountService.hasAnyAuthority(nav.authorities);
            }
            if (nav.feature) {
                enabled = this.featureService.isFeatureEnabled(
                    nav.feature,
                    account,
                );
            }
            if (nav.unlessFeature) {
                enabled = !this.featureService.isFeatureEnabled(
                    nav.unlessFeature,
                    account,
                );
            }

            if (
                this.accountService.hasAnyAuthority(["ROLE_LIMITED_ACCESS"]) &&
                nav.feature === Feature.LEADS
            ) {
                hasAuth = false;
            }
            return hasAuth && enabled;
        });
    }

    anyRouteChildHasNavHidden(route: ActivatedRouteSnapshot) {
        if (route.data && route.data["hidenav"]) {
            return true;
        }
        if (
            route.firstChild &&
            route.firstChild.data &&
            route.firstChild.data["hidenav"]
        ) {
            return true;
        }

        if (!route.firstChild) {
            return false;
        }
        return this.anyRouteChildHasNavHidden(route.firstChild);
    }

    private applyAccountContext(account: IUser | null): void {
        this.currentAccount = account;
        if (account) {
            this.currentUserIsAdmin = this.accountService.hasAnyAuthority([
                "ROLE_ADMIN",
            ]);
            this.currentUserIsLimitedAccess =
                this.accountService.hasAnyAuthority(["ROLE_LIMITED_ACCESS"]);
            this.titleService.setTitle(account.firmName);
        } else {
            this.currentUserIsAdmin = false;
            this.currentUserIsLimitedAccess = false;
        }
    }

    private getPendingRegistrationsCount() {
        return this.clientAccessRegistrationService.getPendingRegistrationsCount();
    }

    updateAccessNavItemWithCount(
        filteredItems: NavData[],
        count: number,
    ): NavData[] {
        return filteredItems.map((item) => {
            if (item.name === "Access Requests") {
                item.badge.text = count > 0 ? count.toString() : null;
            }
            return item;
        });
    }

    ngOnDestroy(): void {
        this.changes.disconnect();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    protected readonly iconSubset = iconSubset;
}
